import React, { useEffect, useState } from 'react';
import api from '../../../../api';
import { FaArrowUp, FaUserAlt, FaUserTie, FaUserNurse, FaUserSecret } from 'react-icons/fa';
import { getRoundRobinBaseUrl } from '../../../../loadBalancer';
import { useAuth } from '../../../Layouts/context/Authcontext';

const brandColors = {
  red: '#EE1D23',
  blue: '#1A2B5C',
  gray: '#808080',
};

const calculateTotals = (list) => {
  const total = list.reduce(
    (acc, curr) => {
      acc.biometric += curr.biometric;
      acc.epr += curr.epr;
      acc.quality += curr.quality;
      return acc;
    },
    { biometric: 0, epr: 0, quality: 0 }
  );
  const count = list.length;
  return {
    biometric: count ? Math.round(total.biometric / count) : 0,
    epr: count ? Math.round(total.epr / count) : 0,
    quality: count ? Math.round(total.quality / count) : 0,
  };
};

const TopBottomAllRounders = ({ filters }) => {
  const { accessToken } = useAuth();
  const [topPerformers, setTopPerformers] = useState([]);
  const [loading, setLoading] = useState(true);

  const buildParams = (filters) => {
    const params = {};
    Object.keys(filters).forEach((key) => {
      if (filters[key]) params[key] = filters[key];
    });
    return params;
  };

  useEffect(() => {
    const fetchPerformers = async () => {
      try {
        setLoading(true);
        const baseUrl = getRoundRobinBaseUrl();
        const headers = { Authorization: `Bearer ${accessToken}` };
        const params = buildParams(filters);

        const topRes = await api.get(`${baseUrl}/superadminDashboard/getTop5OverAllPerformers`, { headers, params });

        const mapData = (data) =>
          data.map((item) => ({
            name: item.employeeName || 'Unknown',
            biometric: parseFloat((item.biometricPercentage || '0').replace('%', '')) || 0,
            epr: parseFloat((item.eprPercentage || '0').replace('%', '')) || 0,
            quality: parseFloat((item.qualityPercentage || '0').replace('%', '')) || 0,
          }));

        if (topRes && topRes.data && topRes.data.success) {
          setTopPerformers(mapData(topRes.data.data || []));
        } else {
          setTopPerformers([]);
        }

      } catch (err) {
        console.error('Error fetching data:', err);
        setTopPerformers([]);
      } finally {
        setLoading(false);
      }
    };
    fetchPerformers();
  }, [accessToken, filters]);

  const medalIcons = ['🥇', '🥈', '🥉'];

  const renderCard = (title, data, total, Icon, color) => (
    <div className="rounded-2xl bg-white shadow-lg p-6 w-full border-t-4 mt-10" style={{ borderColor: color }}>
      <div className="flex items-center mb-4">
        <div className="flex items-center gap-3">
          <div className="text-white p-2 rounded-full" style={{ backgroundColor: color }}>
            <Icon className="w-4 h-4" />
          </div>
          <h2 className="text-base font-semibold" style={{ color }}>
            {title}
          </h2>
        </div>
      </div>

      <ul className="divide-y divide-gray-200 text-sm">
        <li className="flex justify-between py-2 font-medium text-gray-700">
          <span className="flex-1">Name</span>
          <span className="w-20 text-center">Production%</span>
          <span className="w-20 text-center">Quality</span>
        </li>

        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-10 w-10 border-t-4 border-blue-500"></div>
          </div>
        ) : data.length === 0 ? (
          <div className="text-gray-500 text-center py-4 col-span-full">No data available</div>
        ) : (
          data.map((person, i) => (
            <li key={i} className="flex justify-between items-center py-2 px-2 hover:bg-gray-50 transition">
              <span className="flex-1 flex items-center gap-3 overflow-hidden">
                <div
                  className="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm shadow"
                  style={{ backgroundColor: ['#1A2B5C', '#EE1D23', '#808080', '#5567AA'][i % 4] }}
                >
                  {i % 4 === 0 && <FaUserAlt />}
                  {i % 4 === 1 && <FaUserTie />}
                  {i % 4 === 2 && <FaUserNurse />}
                  {i % 4 === 3 && <FaUserSecret />}
                </div>
                <span className="truncate whitespace-nowrap flex items-center gap-1 font-medium">
                  {person.name}
                  {i < 3 && <span className="text-lg">{medalIcons[i]}</span>}
                </span>
              </span>

              <span className="w-20 text-center">{person.biometric}%</span>
              <span className="w-20 text-center">{person.quality}%</span>
            </li>
          ))
        )}

        {!loading && data.length > 0 && (
          <li
            className="flex justify-between items-center py-2 font-medium text-white text-sm rounded-md mt-2 px-2"
            style={{ backgroundColor: color }}
          >
            <span className="flex-1">Average</span>
            <span className="w-20 text-center">{total.biometric}%</span>
            <span className="w-20 text-center">{total.quality}%</span>
          </li>
        )}
      </ul>
    </div>
  );

  return (
    <div className="grid grid-cols-1 gap-6 text-lg mr-80 ml-80">
      {renderCard(
        'All Rounder Top Performers',
        topPerformers,
        calculateTotals(topPerformers),
        FaArrowUp,
        brandColors.blue
      )}
    </div>
  );
};

export default TopBottomAllRounders;
